import React from 'react';
import styled from 'styled-components';

import { Button } from '../button/button';
import { Icon } from '../icon/icon';
import { Stack } from '../stack/stack';

type Props = {
  currentPage: number;
  pageSize: number;
  numberOfItems: number;
  onPageChange: (pageNumber: number) => void;
  /** Whether to render above or below the table. Affects margins. Defaults to 'bottom'. */
  placement?: 'top' | 'bottom';
};

export const getNumberOfPages = (numberOfItems: number, pageSize: number) =>
  numberOfItems % pageSize === 0 ? numberOfItems / pageSize : ((numberOfItems / pageSize) | 0) + 1;

export const Pagination = ({
  currentPage,
  onPageChange,
  pageSize,
  numberOfItems,
  placement = 'bottom',
}: Props) => {
  if (currentPage < 1) {
    throw new Error('CurrentPage must be a positive number greater than 0');
  }

  if (pageSize < 1) {
    throw new Error('PageSize must be a positive number greater than 0');
  }

  if (numberOfItems < 0) {
    throw new Error('numberOfItems must be a positive number');
  }

  const numberOfPages = getNumberOfPages(numberOfItems, pageSize);

  const toNextPage = () => {
    if (currentPage) {
      onPageChange(currentPage < numberOfPages ? currentPage + 1 : currentPage);
    }
  };

  const toLastPage = () => {
    onPageChange(numberOfPages);
  };

  const toPreviousPage = () => {
    if (currentPage) {
      onPageChange(currentPage > 1 ? currentPage - 1 : currentPage);
    }
  };

  const toFirstPage = () => {
    onPageChange(1);
  };

  return (
    <Stack
      alignItems={'center'}
      direction="row"
      justifyContent={'center'}
      margin={placement === 'top' ? '0 0 1.5em 0' : '1.5em 0 0 0'}
      spacing={2}
    >
      <Stack direction="row" justifyContent={'center'} spacing={0.5}>
        <PaginationButton disabled={currentPage === 1} variant={'text'} onClick={toFirstPage}>
          <Icon icon={['fas', 'pipe']} size={'2xs'} />{' '}
          <Icon icon={['fas', 'chevron-left']} size={'2xs'} />{' '}
        </PaginationButton>
        <PaginationButton disabled={currentPage === 1} variant={'text'} onClick={toPreviousPage}>
          <Icon icon={['fas', 'chevron-left']} size={'2xs'} />{' '}
        </PaginationButton>
      </Stack>
      <span className="notranslate">
        {currentPage} / {numberOfPages}
      </span>
      <Stack direction="row" justifyContent={'center'} spacing={0.5}>
        <PaginationButton
          disabled={currentPage === numberOfPages}
          variant={'text'}
          onClick={toNextPage}
        >
          <Icon icon={['fas', 'chevron-right']} size={'2xs'} />{' '}
        </PaginationButton>
        <PaginationButton
          disabled={currentPage === numberOfPages}
          variant={'text'}
          onClick={toLastPage}
        >
          <Icon icon={['fas', 'chevron-right']} size={'2xs'} />{' '}
          <Icon icon={['fas', 'pipe']} size={'2xs'} />{' '}
        </PaginationButton>
      </Stack>
    </Stack>
  );
};

const PaginationButton = styled(Button)`
  border: 1px solid #666666;
  border-radius: 0.5em;
  user-select: none;
`;
